/**
 * Bluetooth thermal printer client using the Web Bluetooth API.
 *
 * Stores the last used device/characteristic in module state so repeat prints
 * don't require re-pairing in the same session.
 */

// Common ESC/POS thermal printer services (most generic RPP/MTP/POS-58 BLE modules)
const PRINTER_SERVICES = [
  0x18f0,
  0xff00,
  0xff10,
  0xffe0,
  0xfff0,
];

const PRINTER_WRITE_CHARS = [
  0x2af1,
  0xff02,
  0xff11,
  0xffe1,
  0xfff2,
];

let cachedDevice = null;
let cachedCharacteristic = null;

export function isBluetoothSupported() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
}

async function findWriteCharacteristic(server) {
  const services = await server.getPrimaryServices();
  for (const service of services) {
    try {
      const chars = await service.getCharacteristics();
      const writable = chars.find(
        (c) => c.properties.write || c.properties.writeWithoutResponse,
      );
      if (writable) return writable;
    } catch (e) {
      // try next service
    }
  }
  throw new Error('No se encontró una característica de escritura en la impresora');
}

export async function connectPrinter() {
  if (!isBluetoothSupported()) {
    throw new Error('Web Bluetooth no está soportado en este navegador');
  }

  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: PRINTER_SERVICES,
  });

  const server = await device.gatt.connect();
  const characteristic = await findWriteCharacteristic(server);

  cachedDevice = device;
  cachedCharacteristic = characteristic;

  device.addEventListener('gattserverdisconnected', () => {
    cachedDevice = null;
    cachedCharacteristic = null;
  });

  return { device, characteristic };
}

export function getConnectedPrinter() {
  if (cachedDevice && cachedDevice.gatt?.connected && cachedCharacteristic) {
    return { device: cachedDevice, characteristic: cachedCharacteristic };
  }
  return null;
}

export async function disconnectPrinter() {
  if (cachedDevice && cachedDevice.gatt?.connected) {
    cachedDevice.gatt.disconnect();
  }
  cachedDevice = null;
  cachedCharacteristic = null;
}

// ---------- ESC/POS builder ----------
const ESC = 0x1b;
const GS = 0x1d;

function encodeLatin(text) {
  // Replace unsupported glyphs and emit CP437/latin-friendly bytes.
  const cleaned = (text || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const bytes = new Uint8Array(cleaned.length);
  for (let i = 0; i < cleaned.length; i += 1) {
    const code = cleaned.charCodeAt(i);
    bytes[i] = code > 0xff ? 0x3f : code; // non-latin -> '?'
  }
  return bytes;
}

function concat(chunks) {
  const total = chunks.reduce((s, c) => s + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

function cmd(...bytes) {
  return new Uint8Array(bytes);
}

function line(text = '') {
  return concat([encodeLatin(text), cmd(0x0a)]);
}

function divider() {
  return line('--------------------------------');
}

/**
 * Build ESC/POS payload for a Car Wash ticket.
 * @param {Object} ticket  { folio, date, client, vehicle, services[], total, payment_method, points_earned, points_redeemed, walk_in }
 */
export function buildTicketCommands(ticket) {
  const chunks = [];

  // Init
  chunks.push(cmd(ESC, 0x40));

  // Center + double-size header
  chunks.push(cmd(ESC, 0x61, 0x01)); // center
  chunks.push(cmd(GS, 0x21, 0x11)); // double w/h
  chunks.push(line('WD LAVADO'));
  chunks.push(cmd(GS, 0x21, 0x00)); // normal
  chunks.push(line('Automotriz'));
  chunks.push(line(''));

  // Folio + date
  chunks.push(cmd(ESC, 0x45, 0x01)); // bold on
  chunks.push(line(String(ticket.folio || '')));
  chunks.push(cmd(ESC, 0x45, 0x00)); // bold off
  if (ticket.date) chunks.push(line(String(ticket.date)));

  // Left-align for body
  chunks.push(cmd(ESC, 0x61, 0x00));
  chunks.push(divider());

  if (ticket.client?.name) {
    chunks.push(line(`Cliente: ${ticket.client.name}`));
  } else if (ticket.walk_in) {
    chunks.push(line('Cliente: Walk-in'));
  }
  if (ticket.client?.phone && ticket.client.phone !== '—') {
    chunks.push(line(`Tel: ${ticket.client.phone}`));
  }
  if (ticket.client?.code) {
    chunks.push(line(`Codigo: ${ticket.client.code}`));
  }
  if (ticket.vehicle) {
    const parts = [ticket.vehicle.type, ticket.vehicle.model, ticket.vehicle.plates].filter(Boolean);
    if (parts.length) chunks.push(line(`Vehiculo: ${parts.join(' ')}`));
  }

  chunks.push(divider());

  (ticket.services || []).forEach((s) => {
    const name = String(s.name || '').slice(0, 22).padEnd(22, ' ');
    const price = `$${Number(s.price || 0).toFixed(2)}`.padStart(9, ' ');
    chunks.push(line(`${name}${price}`));
  });

  chunks.push(divider());

  // Total
  chunks.push(cmd(ESC, 0x45, 0x01));
  const totalLine = `TOTAL: $${Number(ticket.total || 0).toFixed(2)}`;
  chunks.push(line(totalLine));
  chunks.push(cmd(ESC, 0x45, 0x00));

  if (ticket.payment_method) {
    chunks.push(line(`Pago: ${ticket.payment_method}`));
  }
  if (ticket.points_earned > 0) {
    chunks.push(line(`+${ticket.points_earned} puntos ganados`));
  }
  if (ticket.points_redeemed > 0) {
    chunks.push(line(`-${ticket.points_redeemed} puntos usados`));
  }

  chunks.push(line(''));
  chunks.push(cmd(ESC, 0x61, 0x01)); // center
  chunks.push(line('Gracias por su preferencia'));
  chunks.push(line(''));
  chunks.push(line(''));
  chunks.push(line(''));
  chunks.push(line(''));

  // Feed + cut
  chunks.push(cmd(GS, 0x56, 0x00));

  return concat(chunks);
}

export async function writeChunked(characteristic, data, chunkSize = 180) {
  for (let i = 0; i < data.length; i += chunkSize) {
    const slice = data.slice(i, i + chunkSize);
    if (characteristic.writeValueWithoutResponse) {
      // eslint-disable-next-line no-await-in-loop
      await characteristic.writeValueWithoutResponse(slice);
    } else {
      // eslint-disable-next-line no-await-in-loop
      await characteristic.writeValue(slice);
    }
  }
}

export async function printTicketBluetooth(ticket) {
  let printer = getConnectedPrinter();
  if (!printer) printer = await connectPrinter();
  const data = buildTicketCommands(ticket);
  await writeChunked(printer.characteristic, data);
  return printer.device.name || 'Impresora';
}
